import { useState, useEffect, useMemo } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Check, Search } from 'lucide-react'
import debounce from 'lodash.debounce'
import { useSearchPipedriveProducts } from '@Commercial/Pipedrive/hooks/usePipedriveProducts'
import { formattedAutocomplete } from '@Utils/Formatters/formattedAutocomplete'
import { cn } from '@/lib/utils'
import { Alert } from '@/components/ui/alert'

interface ProductOption {
    label: string
    value: any
}

interface PipedriveProductSearchProps {
    control: any
    setValue: any
    state: any
    prefixName: string
    disabled?: boolean
}

export const PipedriveProductSearch = ({
    control,
    setValue,
    state,
    prefixName,
    disabled = false,
}: PipedriveProductSearchProps) => {
    // Estados locais
    const [searchText, setSearchText] = useState('')
    const [term, setTerm] = useState('')
    const [open, setOpen] = useState(false)
    const [selectedValue, setSelectedValue] = useState('')

    // Debounce para reduzir chamadas de API - criado uma única vez
    const updateTerm = useMemo(
        () =>
            debounce((value: string) => {
                setTerm(value)
                if (value.trim()) {
                    setOpen(true)
                }
            }, 500),
        []
    )

    // Busca produtos no Pipedrive
    const { data, isLoading, isError, refetch, isRefetching } =
        useSearchPipedriveProducts(term, { limit: 100 })

    // Processa produtos retornados da API
    const products = useMemo(() => {
        const items = (data?.data?.items || []).map((i: any) => i.item)
        return items
    }, [data])

    // Formata produtos para o componente Command
    const options: ProductOption[] = useMemo(
        () => formattedAutocomplete<any>(products || [], 'name'),
        [products]
    )

    // Restaura texto de busca ao abrir componente
    useEffect(() => {
        if (state?.pipeProductSearch) {
            setSearchText(state.pipeProductSearch)
        }
        if (state?.pipeProduct) {
            setSelectedValue(state.pipeProduct?.name || '')
        }
    }, [state?.pipeProductSearch, state?.pipeProduct])

    // Cleanup do debounce e click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Element
            if (!target.closest('.pipedrive-search-container')) {
                setOpen(false)
            }
        }

        if (open) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            updateTerm.cancel()
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [updateTerm, open])

    // Handler para mudança no input de busca - otimizado para evitar re-renders
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setSearchText(value)
        setValue(`${prefixName}.pipeProductSearch`, value)

        updateTerm(value)
    }

    // Handler para seleção de produto
    const handleProductSelect = (product: ProductOption) => {
        setSelectedValue(product.label)
        setValue(`${prefixName}.pipeProduct`, product.value)
        setSearchText(product.label)
        setValue(`${prefixName}.pipeProductSearch`, product.label)
        setOpen(false)
    }

    // Handler para limpar seleção
    const handleClearSelection = () => {
        setSelectedValue('')
        setSearchText('')
        setValue(`${prefixName}.pipeProduct`, null)
        setValue(`${prefixName}.pipeProductSearch`, '')
        setOpen(false)
    }

    // Handler para abrir/fechar manualmente
    const handleToggleOpen = () => {
        if (!disabled) {
            if (searchText.trim()) {
                setOpen(!open)
            }
        }
    }

    return (
        <div className="relative w-full pipedrive-search-container">
            <div className="flex gap-2">
                <div className="flex-1 relative">
                    <Input
                        placeholder="Digite para buscar produtos no Pipedrive..."
                        value={searchText}
                        onChange={handleSearchChange}
                        disabled={disabled}
                        className="pr-20"
                        onFocus={() => {
                            if (searchText.trim() && options.length > 0) {
                                setOpen(true)
                            }
                        }}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-2">
                        {isLoading && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900" />
                        )}
                        {!isLoading && searchText.trim() && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={handleToggleOpen}
                                disabled={disabled}
                            >
                                <Search className="h-3 w-3" />
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Resultados em dropdown simples */}
            {open && options.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto z-[10000]">
                    {options.map((product, index) => (
                        <button
                            type="button"
                            key={`${
                                product.value?.id || product.label
                            }-${index}`}
                            className="flex items-center gap-2 p-3 hover:bg-secondary cursor-pointer border-b last:border-b-0 w-full text-left"
                            onClick={() => handleProductSelect(product)}
                        >
                            <Check
                                className={cn(
                                    'h-4 w-4 text-green-600',
                                    selectedValue === product.label
                                        ? 'opacity-100'
                                        : 'opacity-0'
                                )}
                            />
                            <div className="flex-1 flex flex-col">
                                <span className="font-medium text-gray-900">
                                    {product.label}
                                </span>
                                <span className="text-sm text-gray-500">
                                    Código: {product.value?.code}
                                </span>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {/* Loading state */}
            {open && isLoading && searchText.trim() && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-[10000]">
                    <div className="p-4 text-sm text-center text-gray-500">
                        Buscando produtos...
                    </div>
                </div>
            )}

            {/* Empty state */}
            {open &&
                !isLoading &&
                searchText.trim() &&
                options.length === 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-[10000]">
                        <div className="p-4 text-sm text-center text-gray-500">
                            Nenhum produto encontrado.
                        </div>
                    </div>
                )}

            {selectedValue && (
                <div className="flex items-center justify-between mt-2 p-2 bg-gray-50 rounded-md">
                    <span className="text-sm font-medium text-gray-900">
                        Selecionado: {selectedValue}
                    </span>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleClearSelection}
                        disabled={disabled}
                        className="h-6 px-2 text-xs"
                    >
                        Limpar
                    </Button>
                </div>
            )}
            {isError && (
                <Alert className="text-red-900 text-sm mt-2 bg-red-200 p-1 flex items-center justify-between">
                    Ops, tive um problema ao buscar os produtos do Pipedrive.
                    Tente novamente ou tente mudar o que está buscando.
                    <Button
                        variant="link"
                        type="button"
                        size="sm"
                        className="text-primary"
                        loading={isLoading || isRefetching}
                        loadingText="Tentando..."
                        onClick={() => {
                            refetch()
                        }}
                    >
                        Tentar novamente
                    </Button>
                </Alert>
            )}
        </div>
    )
}
